import { animate, AnimatePresence, mix, motion, useAnimationFrame, useIsPresent, useMotionValue } from 'motion/react';
import React, { useRef, useState } from 'react';
import { elderFuthark } from '../../../svg/SVGData';
import useDebounce from '../../../../utils/hooks/useDebounce';

const symbols = Object.keys(elderFuthark);
const orbitDuration = 10000; // seconds
const symbolCount = 9;
const animationDuration = 10000;

const radiusRange = mix(0, 48);
const degreeRange = mix(0, 360);

const Symbol: React.FC<{ index: number }> = ({ index }) => {
    const [cursor, setCursor] = useState(index % symbols.length);

    const delay = (index / symbolCount) * orbitDuration;
    const offsetRad = (index / symbolCount) * 2 * Math.PI;
    const offsetDegree = degreeRange(index / symbolCount);

    const x = useMotionValue(0);
    const y = useMotionValue(0);
    const rotateZ = useMotionValue('0deg');

    const tCursor = useRef(0); // for current loop
    useAnimationFrame((t, d) => {
        tCursor.current += d;
        if (tCursor.current > animationDuration) {
            tCursor.current = tCursor.current - animationDuration
        }

        const progressRad = (tCursor.current / animationDuration) * 2 * Math.PI;
        const progressDeg = degreeRange(tCursor.current / animationDuration);
        x.set(radiusRange(Math.cos(progressRad + offsetRad)));
        y.set(radiusRange(Math.sin(progressRad + offsetRad)));
        rotateZ.set(`${progressDeg + offsetDegree + 90}deg`);
    })

    useDebounce(() => {
        setCursor((cursor + 1) % symbols.length);
    }, animationDuration + delay, [cursor])

    // Prevent early visibility
    const [loaded, setLoaded] = useState(false);
    useDebounce(() => {
        setLoaded(true)
    }, delay, [setLoaded])

    // Phantom controls
    const phantomRotateY = useMotionValue('-90deg');

    useDebounce(() => {
        if (!loaded) return;
        animate(phantomRotateY, '0deg', { duration: 2, ease: 'easeIn' })
    }, delay, [setLoaded])

    const phantomIsPresent = useIsPresent();

    useDebounce(() => {
        animate(phantomRotateY, '90deg', { duration: 2, ease: 'easeIn' })
    }, delay / 3, [phantomIsPresent])

    return (
        <AnimatePresence mode='popLayout'>
            {/* Main */}
            <motion.svg
                key={Math.random()}
                layout
                viewBox={elderFuthark[symbols[cursor]].viewBox}
                className='absolute top-0 left-0 h-full'
                style={{
                    x, y,
                    rotateZ
                }}
                initial={{ opacity: 0, rotateY: '-90deg' }}
                animate={loaded ? {
                    opacity: 1,
                    rotateY: '0deg',
                    transition: { duration: 2, ease: 'easeIn' }
                } : {}}
                exit={{
                    opacity: 0,
                    rotateY: '90deg',
                    transition: { duration: 2, ease: 'easeIn' }
                }}

            >
                <motion.path
                    style={{
                        fill: 'none',
                        stroke: 'white',
                        strokeWidth: .75,
                    }}
                    d={elderFuthark[symbols[cursor]].d}
                >
                </motion.path>
            </motion.svg>

            {/* Phantom */}
            <motion.svg
                key={Math.random()}
                layout
                viewBox={elderFuthark[symbols[cursor]].viewBox}
                className='absolute top-0 left-0 h-full'
                style={{
                    x, y,
                    rotateY: phantomRotateY, rotateZ
                }}
                initial={{ opacity: 0 }}
                animate={loaded ? {
                    opacity: .4,
                    transition: { duration: 2, ease: 'easeIn' }
                } : {}}
                exit={{
                    opacity: 0,
                    transition: { duration: 2, ease: 'easeIn' }
                }}

            >
                <motion.path
                    style={{
                        fill: 'none',
                        stroke: 'white',
                        strokeWidth: .75,
                    }}
                    d={elderFuthark[symbols[cursor]].d}
                >
                </motion.path>
            </motion.svg>
        </AnimatePresence>
    )
}

const SymbolRing: React.FC = () => {
    return (
        <motion.div
            className='h-6 aspect-square relative'
        >
            {[...Array(symbolCount).keys()].map(v => <Symbol key={v} index={v} />)}
        </motion.div>
    )
}

export default SymbolRing;